import { bookColors, INSTAGRAM_HANDLE } from "../data/content";

const PAPER = "#FBF9F5";
const INK = "#1C1B1A";
const CRIMSON = "#A63D2E";
const SUBTLE = "#6E6B65";

// Lienzo 1080x1920 (historia de Instagram).
// Instagram tapa ~250px arriba (perfil/hora) y ~340px abajo (campo de
// respuesta y reacciones): todo el contenido vive entre ambas zonas.
const W = 1080;
const H = 1920;
const MARGIN = 110;
const MAX_W = W - MARGIN * 2;
const SAFE_TOP = 262;
const SAFE_BOTTOM = 1580;

const wrapLine = (ctx, text, maxWidth) => {
    const words = text.split(" ");
    const out = [];
    let cur = "";
    for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (cur && ctx.measureText(test).width > maxWidth) {
            out.push(cur);
            cur = w;
        } else {
            cur = test;
        }
    }
    if (cur) out.push(cur);
    return out;
};

const loadFonts = async () => {
    try {
        await Promise.all([
            document.fonts.load('400 29px "Space Mono"'),
            document.fonts.load('300 100px "Cormorant Garamond"'),
            document.fonts.load('400 47px "EB Garamond"'),
        ]);
        await document.fonts.ready;
    } catch {
        /* si falla, el canvas usa serif/mono del sistema */
    }
};

export const sharePoemImage = async (poem) => {
    await loadFonts();
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(28, 27, 26, 0.02)";
    for (let i = 0; i < 3500; i++) {
        ctx.fillRect(Math.random() * W, Math.random() * H, 1.4, 1.4);
    }

    const accent = bookColors[poem.bookId] || CRIMSON;
    ctx.textBaseline = "top";
    if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";

    // Pie de imagen dentro de la zona segura inferior
    const footerTop = SAFE_BOTTOM - 118;
    const FOOTER_LIMIT = footerTop - 42;

    let y = SAFE_TOP;
    ctx.fillStyle = accent;
    ctx.fillRect(MARGIN, y, 72, 6);
    y += 36;
    ctx.font = '400 29px "Space Mono", monospace';
    if ("letterSpacing" in ctx) ctx.letterSpacing = "7px";
    ctx.fillText(`${poem.book.toUpperCase()} · ${poem.year}`, MARGIN, y);
    y += 78;

    if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
    ctx.fillStyle = INK;
    ctx.font = '300 100px "Cormorant Garamond", serif';
    wrapLine(ctx, poem.title, MAX_W).forEach((l) => {
        ctx.fillText(l, MARGIN, y);
        y += 116;
    });
    y += 8;
    ctx.fillStyle = CRIMSON;
    ctx.fillRect(MARGIN, y, 180, 3);
    const poemTop = y + 58;

    const layout = (s) => {
        ctx.font = `400 ${s}px "EB Garamond", serif`;
        const lh = Math.round(s * 1.48);
        const blocks = poem.lines.map((l) => (l === "" ? null : wrapLine(ctx, l, MAX_W)));
        const total = blocks.reduce(
            (acc, b) => acc + (b === null ? lh * 0.5 : b.length * lh),
            0
        );
        return { lh, blocks, total };
    };
    let size = 47;
    let L = layout(size);
    while (L.total > FOOTER_LIMIT - poemTop && size > 26) {
        size -= 2;
        L = layout(size);
    }

    // Centra el poema en el espacio disponible: aire arriba y abajo
    const offset = Math.max(0, (FOOTER_LIMIT - poemTop - L.total) * 0.45);
    y = poemTop + offset;
    ctx.font = `400 ${size}px "EB Garamond", serif`;
    ctx.fillStyle = "rgba(28, 27, 26, 0.92)";
    L.blocks.forEach((b) => {
        if (b === null) {
            y += L.lh * 0.5;
            return;
        }
        b.forEach((l) => {
            ctx.fillText(l, MARGIN, y);
            y += L.lh;
        });
    });

    ctx.fillStyle = CRIMSON;
    ctx.font = '400 30px "Space Mono", monospace';
    ctx.fillText("✳", MARGIN, footerTop);
    if ("letterSpacing" in ctx) ctx.letterSpacing = "5px";
    ctx.font = '400 27px "Space Mono", monospace';
    ctx.fillStyle = SUBTLE;
    ctx.fillText("JOSÉ LUIS GARCÉS MALDONADO", MARGIN, footerTop + 46);
    ctx.fillStyle = CRIMSON;
    ctx.fillText(INSTAGRAM_HANDLE.toUpperCase(), MARGIN, footerTop + 88);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("canvas-empty");
    const filename = `${poem.id}-joseluisgarcespoesia.png`;
    const file = new File([blob], filename, { type: "image/png" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
            files: [file],
            title: `${poem.title} — José Luis Garcés Maldonado`,
        });
        return "shared";
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
};
